import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE, PHONE_TEL, WHATSAPP_URL } from "../data";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-navy/95 shadow-lg shadow-black/25 backdrop-blur-md" : "bg-navy/75 backdrop-blur-sm"
      }`}
    >
      <div className="section-wrap flex h-16 items-center justify-between sm:h-20">
        <a href="#home" data-testid="nav-brand-logo" className="flex items-center">
          <span className="font-serif text-xl font-bold tracking-wide text-cream sm:text-2xl">
            Kasdol <span className="text-gold">Hotel</span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary navigation">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm font-medium text-cream/85 transition-colors hover:text-gold"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#contact" data-testid="nav-cta-book-now" className="btn-gold hidden !px-5 !py-2.5 lg:inline-flex">
            Book Now
          </a>
          <button
            type="button"
            data-testid="nav-hamburger-toggle"
            aria-label="Open navigation menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          className="fixed inset-0 z-[70] flex h-[100dvh] flex-col bg-navy lg:hidden"
        >
          <div className="section-wrap flex h-16 shrink-0 items-center justify-between border-b border-cream/10">
            <span className="font-serif text-xl font-bold tracking-wide text-cream">
              Kasdol <span className="text-gold">Hotel</span>
            </span>
            <button
              type="button"
              data-testid="nav-mobile-close"
              aria-label="Close navigation menu"
              onClick={() => setOpen(false)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-cream/25 text-cream transition-colors hover:border-gold hover:text-gold"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-4" aria-label="Mobile navigation links">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className="block border-b border-cream/10 py-4 font-serif text-2xl text-cream transition-colors hover:text-gold"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="shrink-0 space-y-3 px-6 pb-8 pt-2">
            <a
              href="#contact"
              data-testid="nav-mobile-book-now"
              onClick={() => setOpen(false)}
              className="btn-gold w-full"
            >
              Book Now
            </a>
            <div className="grid grid-cols-2 gap-3">
              <a href={PHONE_TEL} data-testid="nav-mobile-call" className="btn-outline !px-3 !py-3 text-xs">
                <Phone className="h-4 w-4" /> Call {PHONE}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp"
                className="btn-outline !px-3 !py-3 text-xs"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
